import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.css";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import "../homepage/home.css";
import FeatureSection from "./FeatureSection";
import Contact from "./Contact";
import About from "./About";
import FAQ from "../help/FAQ";
import AppFooter from "./AppFooter";
import ImageSlider from "../../components/ImageSlider";
import { useAuth } from "../../base";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import WelcomeVideoModal from "./WelcomeVideoModal";
import PricingSection from "../../plans/PricingSection";
import { handleAnalyticsEvent } from '../../helpers/analyticsHandler';

function Home(props) {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const currentUser = useAuth();
  const [isFAQOpen, setIsFAQOpen] = useState(false);
  const faqRef = useRef(null);
  const isRealAuthenticatedUser = Boolean(currentUser?.uid && !currentUser.email?.startsWith("guest_"));

  useEffect(() => {
    if (!isRealAuthenticatedUser) handleAnalyticsEvent('trial_offer_viewed', 'trial', 'home_hero');
  }, [isRealAuthenticatedUser]);

  // Load the language namespace for AI features if missing
  useEffect(() => {
    if (!i18n.hasResourceBundle(i18n.language, "aiFeatureLang")) {
      i18n
        .loadNamespaces("aiFeatureLang")
        .then(() => console.log("aiFeatureLang namespace loaded"))
        .catch((err) => console.error("Error loading aiFeatureLang namespace:", err));
    }
  }, [i18n]);

  const images = [
    "/maisons-modeles/converted_images/before-ai-generation.webp",
    "/maisons-modeles/converted_images/after-ai-generation.webp",
    "/maisons-modeles/converted_images/after-ai-generation3.webp",
    "/maisons-modeles/converted_images/after-ai-generation2.webp",
  ];

  const openFAQ = () => {
    setIsFAQOpen(true);
    faqRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!location.hash) {
      return;
    }

    const targetId = location.hash.replace("#", "");
    if (!targetId) {
      return;
    }

    if (targetId === "faq") {
      setIsFAQOpen(true);
    }

    const scrollToSection = () => {
      const targetElement = document.getElementById(targetId);
      if (!targetElement) {
        return;
      }

      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      try {
        sessionStorage.setItem(
          "lastIntentionalHashPath",
          `${window.location.pathname}${window.location.search}${window.location.hash}`
        );
      } catch (storageError) {
        console.warn("Unable to persist intentional hash path:", storageError);
      }
    };

    const timeoutId = window.setTimeout(scrollToSection, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [location.hash]);

  return (
    <div>
      {/* Hero Section */}
      <header className="masthead container-fluid text-center text-white py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-7">
              <p className="product-association">{t("appFooter.productStatement")}</p>
              <h1>{t("masthead.transformYourHome")}</h1>
              <p className="masthead-description">{t("masthead.dontJustImagine")}</p>
              {!isRealAuthenticatedUser && (
                <div className="masthead-trial-actions">
                  <button
                    type="button"
                    className="btn btn-light masthead-trial-primary"
                    onClick={() => {
                      handleAnalyticsEvent('trial_cta_clicked', 'trial', 'home_hero');
                      props.openAuthModal('signup', { intent: 'trial', planName: 'standard_plan', source: 'home_hero' });
                    }}
                  >
                    {t('masthead.trialCta')}
                  </button>
                  <button
                    type="button"
                    className="tryGuest btn btn-outline-light"
                    onClick={() => {
                      props.handleGuestLogin();
                      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                    }}
                  >
                    {t('masthead.tryAsGuest')}
                  </button>
                  <small className="masthead-trial-note">{t('masthead.trialNote')}</small>
                </div>
              )}
            </div>
            <div className="col-md-5">
              <WelcomeVideoModal isInline={true} preferMp4={true} />
            </div>
          </div>
        </div>
      </header>

      {/* Feature Section */}
      <section className="feature-section container py-5">
        <h2 className="text-center">{t("aiFeatureLang.intro.title")}</h2>
        <p className="text-center subtitle">{t("aiFeatureLang.intro.description")}</p>
        <div className="row">
          {["step1", "step2", "step3", "step4"].map((step, index) => (
            <div key={index} className="col-lg-3 col-md-6 col-sm-12 text-center mb-4">
              <img
                src={t(`aiFeatureLang.steps.${step}.mediaPath`)}
                alt={t(`aiFeatureLang.steps.${step}.altText`)}
                className="img-fluid mb-3"
              />
              <h5 className="text-primary">{t(`aiFeatureLang.steps.${step}.title`)}</h5>
              <p className="text-muted">{t(`aiFeatureLang.steps.${step}.description`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="home-ai-generation-section container py-5">
        <div className="home-ai-generation-inner">
        <div className="home-ai-generation-header text-center mb-4">
          <h2>{t("homeAiGeneration.title")}</h2>
          <p className="subtitle">{t("homeAiGeneration.description")}</p>
        </div>
        <h3 className="home-ai-visual-title text-center mb-4">{t("homeAiGeneration.visualTitle")}</h3>
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <figure className="home-ai-image-tile">
              <img
                src="/maisons-modeles/converted_images/before-ai-editor-design.webp"
                alt={t("homeAiGeneration.beforeAlt")}
                className="img-fluid rounded shadow-sm"
              />
              <figcaption className="home-ai-image-label">{t("homeAiGeneration.beforeLabel")}</figcaption>
            </figure>
          </div>
          <div className="col-md-6">
            <figure className="home-ai-image-tile">
              <img
                src="/maisons-modeles/converted_images/after-ai-generation.webp"
                alt={t("homeAiGeneration.finalAlt")}
                className="img-fluid rounded shadow-sm"
              />
              <figcaption className="home-ai-image-label">{t("homeAiGeneration.finalLabel")}</figcaption>
            </figure>
          </div>
        </div>
        <div className="row g-4 home-ai-mode-row">
          <div className="col-md-6">
            <div className="card home-ai-mode-card h-100 border-0">
              <div className="card-body">
                <h5 className="text-primary">{t("homeAiGeneration.normalize.title")}</h5>
                <p className="text-muted mb-0">{t("homeAiGeneration.normalize.description")}</p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card home-ai-mode-card h-100 border-0">
              <div className="card-body">
                <h5 className="text-primary">{t("homeAiGeneration.realistic.title")}</h5>
                <p className="text-muted mb-0">{t("homeAiGeneration.realistic.description")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="home-ai-prompt-sequence mt-4">
          <h4 className="home-ai-prompt-title text-center">{t("homeAiGeneration.promptSequence.title")}</h4>
          <ol className="home-ai-prompt-list">
            <li>{t("homeAiGeneration.promptSequence.step1")}</li>
            <li>{t("homeAiGeneration.promptSequence.step2")}</li>
            <li>{t("homeAiGeneration.promptSequence.step3")}</li>
            <li>{t("homeAiGeneration.promptSequence.step4")}</li>
          </ol>
        </div>
        <div className="text-center mt-4">
          {!isRealAuthenticatedUser && (
            <a
              href="#guest"
              className="btn btn-outline-primary"
              onClick={(e) => {
                e.preventDefault();
                props.handleGuestLogin();
                setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 100);
              }}
            >
              {t("homeAiGeneration.cta")}
            </a>
          )}
        </div>
        </div>
      </section>

      {/* Slider Section */}
      <section className="slider-section container-fluid py-5 bg-light">
        <div className="container">
          <h2 className="text-center showcase-title mb-4">{t("slider.title")}</h2>
          <div className="row">
            <div className="col-md-6">
              <img
                src={images[0]}
                alt="Original House"
                className="img-fluid rounded shadow-sm"
              />
            </div>
            <div className="col-md-6">
              <ImageSlider images={images} />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="howitworks" className="how-it-works-section py-5">
        <div className="container">
          <FeatureSection
            titleKey="homepage.how_it_works.title"
            descriptionKeys={[
              "homepage.how_it_works.steps.step1",
              "homepage.how_it_works.steps.step2",
              "homepage.how_it_works.steps.step3",
              "homepage.how_it_works.steps.step4",
            ]}
            handleGuestLogin={props.handleGuestLogin}
            mediaKey="feature1.mediaPath"
          />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section py-5">
        <div className="container">
          <About />
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section-wrapper">
        <PricingSection 
          handleGuestLogin={props.handleGuestLogin} 
          userRole={props.userRole} 
          openAuthModal={props.openAuthModal}
          requestEditorExitConfirmation={props.requestEditorExitConfirmation}
        />
      </section>

      <section id="contact" className="contact-section container-fluid py-5 bg-light">
        <div className="container d-flex flex-column align-items-center">
          <Contact openFAQ={openFAQ} />
        </div>
      </section>

      <section id="faq" className="faq-section container-fluid py-5">
        <div className="container">
          <div ref={faqRef}>
            <FAQ isFAQOpen={isFAQOpen} />
          </div>
        </div>
      </section>
      <AppFooter />
    </div>
  );
}

export default Home;
